import React, { useState, useCallback, useEffect, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import { compareTwoStrings } from 'string-similarity';
import PropTypes from 'prop-types';
import { translate } from '../Services/translation';
import StoreLocatorMarker from './StoreLocatorMarker';
import { DEFAULT_MAP_CENTER, MAP_COMPONENT_RESTRICTIONS } from './StoreLocator';
import useDebounceCallback from '../Hooks/useDebounceCallback';

const DEBOUNCE_DELAY = 500;

const MIN_ZOOM = 4.5;

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

const CheckoutStorePickup = (props) => {
  const { stores: mapStores, defaultStore, setSelectedReseller } = props;
  const defaultStoreIndex = defaultStore
    ? mapStores.findIndex(
        (store) => store.organization == defaultStore.organization
      )
    : null;

  //List stores for sidebar
  const [listStores, setListStores] = useState(mapStores);

  const [customerLocation, setCustomerLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryFlag, setSearchQueryFlag] = useState(false);

  const [customerPrediction, setCustomerPrediction] = useState(null);

  const [activeMarkerIndex, setActiveMarkerIndex] = useState(-1);

  const [mapReference, setMapReference] = useState(null);
  const [mapsReference, setMapsReference] = useState(null);

  const [selectedStoreIndex, setSelectedStoreIndex] = useState(null); // store index

  const [initialBounds, setInitialBounds] = useState(null);

  const searchInput = useRef(null);
  const searchBox = useRef(null);

  useDebounceCallback(
    searchQuery,
    () => {
      if (
        Boolean(customerPrediction) &&
        customerPrediction.description === searchQuery
      ) {
        return;
      } else {
        const keyboardEvent = new KeyboardEvent('keydown', {
          code: 'Enter',
          key: 'Enter',
          charCode: 13,
          keyCode: 13,
          view: window,
          bubbles: true,
        });
        searchInput.current.dispatchEvent(keyboardEvent);
      }
    },
    DEBOUNCE_DELAY,
    {
      skipFirstRender: true,
    }
  );

  useEffect(() => {
    if (mapReference && mapsReference && defaultStoreIndex) {
      setSelectedStoreIndex(defaultStoreIndex);
    }
  }, [setSelectedStoreIndex, mapReference, mapsReference, defaultStoreIndex]);

  useEffect(() => {
    if (selectedStoreIndex !== null) {
      const pickedStore = mapStores.find(
          (store) => store.index == selectedStoreIndex
      );

      if (pickedStore) {
        setSelectedReseller({ ...pickedStore });  // Only spread if pickedStore is found
      } else {
        // Handle case where no store is found for the given index
        setSelectedReseller(null);  // Or handle however necessary for your use case
      }
    }
  }, [selectedStoreIndex]);

  useEffect(() => {
    let tempStores = mapStores;
    let pickedStore = null;

    if (selectedStoreIndex !== null) {
      pickedStore = tempStores.find((store) => store.index == selectedStoreIndex);
    }

    let centerLocation = customerLocation;
    if (pickedStore && mapsReference) {
      centerLocation = new mapsReference.LatLng(pickedStore.lat, pickedStore.lng);
    }

    // Nearest filter
    if (centerLocation && mapReference && mapsReference) {
      let nearestStores = [];
      tempStores.forEach((store) => {
        const storeLocation = new mapsReference.LatLng(store.lat, store.lng);
        nearestStores.push({
          index: store.index,
          distance: mapsReference.geometry.spherical.computeDistanceBetween(
            centerLocation,
            storeLocation
          ),
          lat: store.lat,
          lng: store.lng,
        });
      });
      nearestStores.sort((a, b) => {
        return a.distance - b.distance;
      });
      nearestStores = nearestStores.slice(0, 4);

      const nearestStoresIndexes = nearestStores.map((store) => store.index);
      tempStores = tempStores.filter((store) =>
        nearestStoresIndexes.includes(store.index)
      );
      tempStores.sort(
        (a, b) =>
          nearestStoresIndexes.indexOf(a.index) -
          nearestStoresIndexes.indexOf(b.index)
      );

      if (!pickedStore) {
        const bounds = new mapsReference.LatLngBounds();
        bounds.extend(centerLocation);
        for (let i = 0; i < tempStores.length; i++) {
          const position = new mapsReference.LatLng(
            tempStores[i].lat,
            tempStores[i].lng
          );
          bounds.extend(position);
        }
        mapReference.fitBounds(bounds);
      }
    }

    if (pickedStore) {
      const alreadyExists = tempStores.find(
        (store) => store.index == selectedStoreIndex
      );
      if (!alreadyExists) {
        tempStores = tempStores.slice(0, 3);
        tempStores.unshift(pickedStore);
      }
    }

    setListStores(tempStores);
  }, [
    mapStores,
    setListStores,
    customerLocation,
    selectedStoreIndex,
    mapReference,
    mapsReference,
  ]);

  const handleOnPlacesChanged = useCallback(() => {
    setCustomerLocation(null);
    setSelectedStoreIndex(null);
    setActiveMarkerIndex(-1);

    const place = searchBox.current.getPlace();
    if (place.geometry) {
      const location = new mapsReference.LatLng(
        place.geometry.location.lat(),
        place.geometry.location.lng()
      );
      mapReference.panTo(location);
      setCustomerLocation(location);
    } else if (!place.geometry && place.name) {
      const autocompleteService = new mapsReference.places.AutocompleteService();
      const placesService = new mapsReference.places.PlacesService(mapReference);
      autocompleteService.getPlacePredictions(
        {
          input: place.name,
          ...MAP_COMPONENT_RESTRICTIONS,
        },
        (predictions, status) => {
          if (
            status == mapsReference.places.PlacesServiceStatus.OK &&
            predictions &&
            !!predictions.length
          ) {
            const newPredictions = predictions
              .map((pred) => ({
                ...pred,
                sim: compareTwoStrings(
                  place.name.toLocaleLowerCase(),
                  pred.structured_formatting.main_text.toLocaleLowerCase()
                ),
              }))
              .sort((a, b) => b.sim - a.sim);

            const bestPrediction = newPredictions[0];

            if (!bestPrediction || !bestPrediction?.reference) {
              setCustomerLocation(null);
              return;
            }

            placesService.getDetails(
              {
                reference: bestPrediction.reference,
              },
              (details, status) => {
                if (
                  status == mapsReference.places.PlacesServiceStatus.OK &&
                  details &&
                  details.geometry
                ) {
                  const location = new mapsReference.LatLng(
                    details.geometry.location.lat(),
                    details.geometry.location.lng()
                  );
                  mapReference.panTo(location);
                  setCustomerPrediction(bestPrediction);
                  setCustomerLocation(location);
                  setSearchQueryFlag(true);
                } else {
                  setCustomerLocation(null);
                }
              }
            );
          } else {
            setCustomerLocation(null);
          }
        }
      );
    } else {
      setCustomerLocation(null);
    }
  }, [searchBox, mapReference, mapsReference]);

  useEffect(() => {
    if (!searchQuery && mapReference && initialBounds) {
      mapReference.fitBounds(initialBounds);
    }
  }, [initialBounds, mapReference, mapsReference, searchQuery]);

  useEffect(() => {
    if (!searchBox.current && mapsReference) {
      searchBox.current = new mapsReference.places.Autocomplete(
        searchInput.current,
        {
          ...MAP_COMPONENT_RESTRICTIONS,
        }
      );
      searchBox.current.addListener('place_changed', handleOnPlacesChanged);
    }

    return () => {
      if (mapsReference) {
        searchBox.current = null;
        mapsReference.event.clearInstanceListeners(searchBox);
      }
    };
  }, [searchInput, searchBox, mapsReference, handleOnPlacesChanged]);

  const searchInputOnChange = (e) => setSearchQuery(e.target.value);
  const onSelectedStoreChange = (index) => {
    onMapChildClick(index);
  };

  /* Map */
  const onMarkerClose = () => {
    setActiveMarkerIndex(-1);
  };

  const onMapChildClick = (index) => {
    setSelectedStoreIndex(index);
    const store = mapStores.find((store) => store.index == index);
    if (store) {
      mapReference.panTo({
        lat: store.lat,
        lng: store.lng,
      });
      const mapDivHeight = mapReference.getDiv().offsetHeight;
      if (mapDivHeight < 400) {
        mapReference.panBy(0, -mapDivHeight / 3);
      }
    }
    setActiveMarkerIndex(index);
  };

  const onDealerSelect = e => {
    props.onDealerSelect(e);
  }

  const onGoogleApiLoaded = ({ map, maps }) => {
    setMapReference(map);
    setMapsReference(maps);

    const bounds = new maps.LatLngBounds();
    for (let i = 0; i < mapStores.length; i++) {
      const position = new maps.LatLng(mapStores[i].lat, mapStores[i].lng);
      bounds.extend(position);
    }
    map.fitBounds(bounds); // Automatically center the map fitting all markers on the screen

    setInitialBounds(bounds);
  };

  const createMapOptions = () => {
    return {
      streetViewControl: true,
      clickableIcons: false,
      minZoom: MIN_ZOOM,
      styles: [
        {
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
      ],
    };
  };

  const { width } = useWindowDimensions();

  return (
    <div className="store-picker-component">
      <div className="store-picker-search-column">
        <div className="search-action-wrapper">
          <input
            ref={searchInput}
            className="locations-search-input"
            type="text"
            placeholder={translate('checkout.search')}
            onChange={searchInputOnChange}
            value={searchQuery || ''}
          />
        </div>
        {searchQueryFlag && (
          <div className="search-result-wrapper">
            <ul className="search-results">
              {listStores.map((store) => (
                <li
                  key={store.index}
                  onClick={() => onSelectedStoreChange(store.index)}
                >
                  <div className="store-wrap">
                    <div className="store-radio-wrap">
                      <label
                        className={`store-radio ${
                          selectedStoreIndex == store.index ? 'selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          value={store.index}
                          checked={selectedStoreIndex == store.index}
                          onChange={() => {}}
                        />
                        <span className="fake-radio"></span>
                      </label>
                    </div>
                    <div className="store-content-wrap">
                      <div className="store-name">{store.name}</div>
                      <div className="store-company">{store.company}</div>
                      <div className="store-address">{store.address}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="store-picker-map-column">
        <GoogleMapReact
          bootstrapURLKeys={{
            key: window?.__litium?.constants?.googleMapApiKey,
            libraries: ['places', 'geometry'],
          }}
          defaultCenter={DEFAULT_MAP_CENTER}
          defaultZoom={MIN_ZOOM}
          onChildClick={onMapChildClick}
          onGoogleApiLoaded={onGoogleApiLoaded}
          yesIWantToUseGoogleMapApiInternals={true}
          options={createMapOptions}
        >
          {mapStores.map((store) => (
            <StoreLocatorMarker
              key={store.index}
              lat={store.lat}
              lng={store.lng}
              store={store}
              show={store.index == activeMarkerIndex}
              mapsReference={mapsReference}
              onClose={onMarkerClose}
              onDealerSelect={onDealerSelect}
            />
          ))}
        </GoogleMapReact>
      </div>
    </div>
  );
};

export default CheckoutStorePickup;

const StoreType = PropTypes.shape({
  index: PropTypes.number,
  organization: PropTypes.string,
  name: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string,
  zip: PropTypes.string,
  lat: PropTypes.number,
  lng: PropTypes.number,
  isPickup: PropTypes.bool,
});

CheckoutStorePickup.propTypes = {
  stores: PropTypes.arrayOf(StoreType).isRequired,
  defaultStore: StoreType,
  setSelectedReseller: PropTypes.func.isRequired,
};
